import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Phone } from 'lucide-react';
import { normalizeIndianMobile, getIndianMobileError } from '../../utils/phone';

/**
 * Reusable Indian Mobile Number Input Component
 * 
 * Props:
 * - id: Input ID
 * - name: Input name
 * - value: Controlled value
 * - onChange: onChange handler (receives normalized value)
 * - onBlur: Optional onBlur handler
 * - label: Label text (optional)
 * - placeholder: Placeholder text
 * - required: Whether field is required (default: true)
 * - error: External error message (optional, overrides internal validation)
 * - className: Additional CSS classes
 * - iconClassName: Icon CSS classes
 * - showError: Whether to show error message (default: true)
 */
const IndianMobileInput = ({
  id,
  name,
  value = '',
  onChange,
  onBlur,
  label,
  placeholder = 'Enter 10-digit mobile number',
  required = true,
  error: externalError,
  className = '',
  iconClassName = '',
  showError = true,
  ...props
}) => {
  const [internalError, setInternalError] = useState(null);
  const [touched, setTouched] = useState(false);

  // Validate on value change if touched
  useEffect(() => {
    if (touched || value) {
      const error = getIndianMobileError(value, required);
      setInternalError(error);
    } else {
      setInternalError(null);
    }
  }, [value, required, touched]);

  const handleChange = (e) => {
    const rawValue = e.target.value;
    const normalized = normalizeIndianMobile(rawValue);
    
    // Create synthetic event with normalized value
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        name: name || id,
        value: normalized
      }
    };
    
    onChange(syntheticEvent);
  };

  const handleBlur = (e) => {
    setTouched(true);
    const error = getIndianMobileError(value, required);
    setInternalError(error);
    
    if (onBlur) {
      onBlur(e);
    }
  };

  const error = externalError || internalError;
  const showErrorMessage = showError && touched && error;

  return (
    <div className="space-y-2">
      {label && (
        <Label htmlFor={id} className="text-white">
          {label}
          {required && <span className="text-red-400">*</span>}
        </Label>
      )}
      <div className="relative">
        <Phone className={`absolute left-3 top-3 h-4 w-4 text-gray-400 ${iconClassName}`} />
        <Input
          id={id}
          name={name || id}
          type="tel"
          inputMode="numeric"
          pattern="[6-9][0-9]{9}"
          maxLength={10}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`pl-10 bg-gray-800 border-gray-600 text-white ${error ? 'border-red-500' : ''} ${className}`}
          required={required}
          {...props}
        />
      </div>
      {showErrorMessage && (
        <p className="text-xs text-red-400">{error}</p>
      )}
    </div>
  );
};

export default IndianMobileInput;

